import { h, Fragment } from "preact";
import { useRef, useState } from "preact/hooks";
import ProductOptions from "./product-options/product-options";
import QuantityInput from "../global/quantity-input/quantity-input-react";
import ProductOptionsMulti from "./product-options/product-options-multi";
import { useEffect } from "react";
import ProductAddons from "./product-addons/product-addons";
import MonogramTrigger from "./monogram-trigger/monogram-trigger";
import StockMessage from "./stock-message";
import StickyAtc from "./sticky-atc";
import useProduct from "../helpers/useProduct/useProduct";
import fetchProduct from "../helpers/useProduct/fetchProduct";
import GiftCardRecipientTrigger from "./gift-card-recipient-form/gift-card-recipient-form-trigger";
import GiftCardTypeToggle, {
  GIFT_CARD_TYPES,
} from "./gift-card-type/gift-card-type-toggle";
import monogramLineItems, {
  monogramQuantity,
} from "./monogram/monogramLineItems";
import { monogramHandleFor } from "./monogram/monogramTypeConfig";
import returnPolicyProperties from "../helpers/returnPolicyProperties";
import {
  ADD_TO_CART_SOURCES,
  cartItemTracking,
  sourceFromVariant,
} from "../helpers/dataLayer";

const OPTIONS_STYLES = {
  default: "default",
  multiSelect: "multi-select",
};

const LAYOUTS = {
  default: "default",
  inline: "inline",
};

const PRICE_SNIPPET_ATTRIBUTES = [
  "dynamic",
  "price",
  "compare-at-price",
  "currency",
];

const INITIAL_GIFTCARD_ERRORS = {
  email: false,
  emailIsInvalid: false,
  send_on: false,
};

export default function ProductFormContent({
  productId,
  idPrefix,
  initialVariantId,
  optionsStyle,
  layout,
  addonLimit,
  smallMonogram = false,
  isGiftCard,
  physicalGiftCardProductId,
  swatchRequestEnabled,
  showAtcPrice = true,
  stickyAtc = false,
  truncateOptions = false,
  addToCartProperties,
  onTotalChange,
  onVariantChange,
}) {
  const atcRef = useRef(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [additionalVariants, setAdditionalVariants] = useState([]);
  const [previewVariant, setPreviewVariant] = useState(null);
  // Configured monogram for the main product, as { data, variant, selection,
  // placement } — the values for display/cart, the variant it prices to, the
  // selection used to reopen the modal when editing, and the selected placement
  // metaobject (its lead time drives the stock message). Null until one is added.
  const [monogram, setMonogram] = useState(null);
  const [addonVariants, setAddonVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isInline = layout === LAYOUTS.inline;
  const isMultiSelect = optionsStyle === OPTIONS_STYLES.multiSelect;

  // The variant the form currently represents for display purposes. Multi-select
  // builds a list of variants rather than selecting one, so it reports the
  // option value being previewed instead.
  const displayVariant = isMultiSelect ? previewVariant : selectedVariant;

  // Swatch lists truncate in the space-constrained forms, never on the main
  // product form.
  const truncate = truncateOptions || isInline;

  const [giftCardType, setGiftCardType] = useState(GIFT_CARD_TYPES.digital);

  const giftCardTypesEnabled = !!isGiftCard && !!physicalGiftCardProductId;
  const isPhysicalGiftCard =
    giftCardTypesEnabled && giftCardType === GIFT_CARD_TYPES.physical;

  const activeProductId = isPhysicalGiftCard
    ? physicalGiftCardProductId
    : productId;

  // Null until the first type switch, so the variant the page was rendered for
  // still wins on load.
  const [preferredOptionValues, setPreferredOptionValues] = useState(null);

  // The inline layout renders the product title, price and image itself, so it
  // needs the product data up front rather than waiting on a variant selection.
  const [productData] = useProduct(isInline ? activeProductId : null);

  // Gift card recipient form handling
  const [recipientData, setRecipientData] = useState({});
  const [recipientErrors, setRecipientErrors] = useState(
    INITIAL_GIFTCARD_ERRORS,
  );
  const [isGift, setIsGift] = useState(isGiftCard);

  const resetRecipientErrors = () =>
    setRecipientErrors(INITIAL_GIFTCARD_ERRORS);

  // Each type is its own product, so anything configured against the outgoing
  // one is dropped — bar its option values, which carry over to be matched.
  const changeGiftCardType = (type) => {
    if (type === giftCardType) return;

    setPreferredOptionValues(
      Object.fromEntries(
        (selectedVariant?.selectedOptions || []).map(({ name, value }) => [
          name,
          value,
        ]),
      ),
    );
    setGiftCardType(type);
    setSelectedVariant(null);
    setPreviewVariant(null);
    setAdditionalVariants([]);
    setAddonVariants([]);
    setMonogram(null);
    setQuantity(1);
    setIsGift(type === GIFT_CARD_TYPES.digital);
    resetRecipientErrors();
  };

  // Warms the cache so the first toggle swaps options in the same frame the
  // click lands, instead of emptying them while the request goes out.
  useEffect(() => {
    if (!giftCardTypesEnabled) return;

    fetchProduct(physicalGiftCardProductId);
  }, [giftCardTypesEnabled, physicalGiftCardProductId]);

  useEffect(() => setAddedToCart(false), [addedToCart]);

  const showError = (error) => {
    setError(error);
    setTimeout(() => {
      setError(null);
    }, 3000);
  };

  const allItems = [
    { variant: selectedVariant, quantity, monogram },
    ...additionalVariants,
    ...addonVariants.map((item) => ({ ...item, addon: true })),
  ];

  const totalPrice = allItems.reduce((total, item) => {
    const { variant, quantity, monogram } = item;
    if (!variant) return total;

    let sum = total + parseFloat(variant.price.amount) * quantity;
    if (monogram?.variant) {
      sum += parseFloat(monogram.variant.price.amount) * monogramQuantity(item);
    }
    return sum;
  }, 0);

  const totalQuantity = allItems.reduce((total, curr) => {
    if (!curr.variant) return total;

    return total + curr.quantity;
  }, 0);

  const hasAvailableVariant = allItems.some(
    ({ variant }) => variant?.availableForSale,
  );

  const currencyCode = allItems.find(({ variant }) => variant)?.variant?.price
    ?.currencyCode;

  // Lets a parent display the same total the add to bag button prices to.
  useEffect(() => {
    if (!onTotalChange || !currencyCode) return;

    onTotalChange({ amount: totalPrice, currencyCode });
  }, [onTotalChange, totalPrice, currencyCode]);

  // Lets a parent show the variant the form is configured to. Only reports real
  // variants, so an unavailable combination — or a remount — leaves the parent
  // showing the last known one rather than blanking out.
  useEffect(() => {
    if (!onVariantChange || !displayVariant) return;

    onVariantChange(displayVariant);
  }, [onVariantChange, displayVariant]);

  // The physical gift card's options never morph the page, so the header price
  // is driven client-side while it's selected and put back on switching to
  // digital — whose variant is usually the one already rendered, so no morph
  // would restore it either.
  const serverPriceMarkup = useRef(new Map());

  useEffect(() => {
    if (!giftCardTypesEnabled || isInline) return;

    const saved = serverPriceMarkup.current;

    if (!isPhysicalGiftCard) {
      saved.forEach((markup, el) => {
        PRICE_SNIPPET_ATTRIBUTES.forEach((name) => el.removeAttribute(name));
        el.innerHTML = markup;
      });
      saved.clear();
      return;
    }

    if (!selectedVariant) return;

    const { price, compareAtPrice } = selectedVariant;

    document.querySelectorAll(".product-details__price").forEach((el) => {
      if (!saved.has(el)) saved.set(el, el.innerHTML);

      el.setAttribute("price", price.amount);
      el.setAttribute("currency", price.currencyCode);
      if (compareAtPrice) {
        el.setAttribute("compare-at-price", compareAtPrice.amount);
      } else {
        el.removeAttribute("compare-at-price");
      }
      // Last, so the snippet renders once with every value in place.
      el.setAttribute("dynamic", "");
    });
  }, [giftCardTypesEnabled, isInline, isPhysicalGiftCard, selectedVariant]);

  const toLineItems = (item) => {
    const { variant, monogram, addon } = item;
    if (!variant?.availableForSale) return [];

    const properties = returnPolicyProperties(variant.product?.tags);
    const tracking = cartItemTracking(sourceFromVariant(variant), {
      addon,
      hasMonogram: !!monogram,
    });

    if (!monogram) {
      return [
        {
          id: window.gidToId(variant.id),
          quantity: item.quantity,
          properties,
          tracking,
        },
      ];
    }
    if (!monogram.variant?.availableForSale) return [];

    const [productLine, monogramLine] = monogramLineItems({
      variant,
      quantity: item.quantity,
      monogram,
      properties,
    });

    return [
      { ...productLine, tracking },
      {
        ...monogramLine,
        tracking: cartItemTracking(sourceFromVariant(monogram.variant)),
      },
    ];
  };

  const onAdd = (sourceProperties) => {
    // Reverse, so order in cart matches order on form.
    let items = allItems.flatMap(toLineItems).reverse();

    if (isGift) {
      resetRecipientErrors();

      items = items.map((item) => ({
        ...item,
        properties: {
          ...item.properties,
          "Recipient email": recipientData.email,
          "Recipient name": recipientData.name,
          Message: recipientData.message,
          "Send on": recipientData.send_on,
          __shopify_send_gift_card_to_recipient: "true",
        },
      }));
    }

    setLoading(true);
    window.addToCart(items, {
      tracking: {
        properties: { ...addToCartProperties, ...sourceProperties },
      },
      onError: (response) => {
        setLoading(false);

        if (response.errors?.email || response.errors?.send_on) {
          setRecipientErrors({
            ...recipientErrors,
            email: !!response.errors.email,
            emailIsInvalid: response.errors.email
              ? response.errors.email[0].includes("invalid")
              : false,
            send_on: !!response.errors.send_on,
          });

          return;
        }
        if (response?.message) {
          console.warn("Error adding to cart", response.message);
        }
        showError(window.localeStrings.product.add_to_cart_error);
      },
      onAfterAdd: () => {
        setLoading(false);
        setQuantity(1);
        setMonogram(null);
        setAddedToCart(true);
        document.dispatchEvent(new CustomEvent("mini-cart:open"));
      },
    });
  };

  const monogramHandle = monogramHandleFor(selectedVariant);

  // --- Shared pieces, arranged differently per layout ------------------------

  const options = isMultiSelect ? (
    <ProductOptionsMulti
      productId={productId}
      idPrefix={idPrefix}
      initialVariantId={initialVariantId}
      selectedVariants={additionalVariants}
      setSelectedVariants={setAdditionalVariants}
      setPreviewVariant={setPreviewVariant}
      addedToCart={addedToCart}
      truncate={truncate}
      smallMonogram={smallMonogram || isInline}
      swatchRequestEnabled={swatchRequestEnabled}
    />
  ) : (
    // Secondary for physical, so its options don't morph the page over to the
    // physical product's URL — this page stays the digital gift card's.
    <ProductOptions
      productId={activeProductId}
      idPrefix={idPrefix}
      initialVariantId={initialVariantId}
      preferredOptionValues={preferredOptionValues}
      optionsRole={isPhysicalGiftCard ? "secondary" : "primary"}
      selectedVariant={selectedVariant}
      setSelectedVariant={setSelectedVariant}
      truncate={truncate}
      swatchRequestEnabled={swatchRequestEnabled}
    />
  );

  // The standalone monogram trigger only applies to the default options-style;
  // multi-select attaches monograms per line inside ProductOptionsMulti.
  const monogramTrigger = isMultiSelect ? null : (
    <MonogramTrigger
      handle={monogramHandle}
      value={monogram}
      onChange={setMonogram}
      currentVariant={displayVariant}
      small={smallMonogram || isInline}
    />
  );

  const giftCardTypeToggle = giftCardTypesEnabled ? (
    <GiftCardTypeToggle
      idPrefix={idPrefix}
      value={giftCardType}
      onChange={changeGiftCardType}
    />
  ) : null;

  const giftCardRecipientTrigger =
    isGiftCard && !isPhysicalGiftCard ? (
      <GiftCardRecipientTrigger
        idPrefix={idPrefix}
        isGift={isGift}
        setIsGift={setIsGift}
        recipientData={recipientData}
        setRecipientData={setRecipientData}
        resetRecipientErrors={resetRecipientErrors}
        recipientErrors={recipientErrors}
      />
    ) : null;

  const addons = (
    <ProductAddons
      productId={activeProductId}
      idPrefix={idPrefix}
      mainVariant={selectedVariant}
      selectedVariants={addonVariants}
      setSelectedVariants={setAddonVariants}
      addedToCart={addedToCart}
      addonLimit={addonLimit}
    />
  );

  const stockMessage = (
    <StockMessage variant={selectedVariant} monogram={monogram} />
  );

  const atcDisabled = loading || totalQuantity === 0 || !hasAvailableVariant;

  const atcLabel = showAtcPrice
    ? window.localeStrings.product.add_to_cart_price.replace(
        "[price]",
        window.formatMoney(totalPrice, false, currencyCode),
      )
    : window.localeStrings.product.add_to_cart;

  const actions = (
    <div className="product-form__actions-wrapper">
      <div class="product-form__actions">
        {selectedVariant && (
          <QuantityInput
            quantity={quantity}
            setQuantity={setQuantity}
            className="product-form__quantity"
          />
        )}
        <button
          className="product-form__atc button button--black"
          ref={atcRef}
          onClick={() => onAdd()}
          disabled={atcDisabled}
          type="button"
          dangerouslySetInnerHTML={{ __html: atcLabel }}
        ></button>
      </div>
      {error && (
        <div
          className="product-form__error"
          dangerouslySetInnerHTML={{
            __html: error,
          }}
        ></div>
      )}
    </div>
  );

  if (isInline) {
    const inlineProduct = selectedVariant?.product || productData;
    const inlineImageUrl =
      displayVariant?.image?.url || inlineProduct?.featuredImage?.url || null;
    const inlineImageAlt =
      displayVariant?.image?.altText ||
      inlineProduct?.featuredImage?.altText ||
      inlineProduct?.title ||
      "";

    const media = (
      <div className="product-form__inline-media">
        {inlineImageUrl && (
          <img
            className="product-form__inline-image"
            src={window.getSizedImageUrl(inlineImageUrl, "800x")}
            alt={inlineImageAlt}
            loading="lazy"
          />
        )}
      </div>
    );

    const title = inlineProduct?.title && (
      <h2 className="product-form__inline-product-title">
        <a href={inlineProduct.helpers?.url}>{inlineProduct.title}</a>
      </h2>
    );

    if (isMultiSelect) {
      return (
        <div className="product-form__inline product-form__inline--multi">
          {media}
          <div className="product-form__inline-title">{title}</div>
          <div className="product-form__inline-content">
            {giftCardTypeToggle}
            {options}
            {addons}
            {giftCardRecipientTrigger}
            {actions}
          </div>
        </div>
      );
    }

    const inlinePrice = selectedVariant
      ? parseFloat(selectedVariant.price.amount)
      : inlineProduct?.helpers
        ? parseFloat(inlineProduct.helpers.price)
        : null;

    return (
      <div className="product-form__inline">
        {media}
        <div className="product-form__inline-title">
          {title}
          {inlinePrice != null && (
            <div className="product-form__inline-price">
              {window.formatMoney(
                inlinePrice,
                false,
                currencyCode || inlineProduct?.helpers?.currency,
              )}
            </div>
          )}
        </div>
        <div className="product-form__inline-options">
          {giftCardTypeToggle}
          {options}
        </div>
        <div className="product-form__inline-aside">
          {monogramTrigger}
          {addons}
          {giftCardRecipientTrigger}
          {stockMessage}
          {actions}
        </div>
      </div>
    );
  }

  return (
    <>
      {giftCardTypeToggle}
      {options}
      {giftCardRecipientTrigger}
      {monogramTrigger}
      {addons}
      {stockMessage}
      {actions}
      {stickyAtc && (
        <StickyAtc
          triggerRef={atcRef}
          variant={displayVariant}
          isMultiSelect={isMultiSelect}
          label={atcLabel}
          disabled={atcDisabled}
          onAdd={() => onAdd(ADD_TO_CART_SOURCES.stickyAtc)}
        />
      )}
    </>
  );
}
